import type { FaqItem } from "../faq";

export type ReviewRecord = {
  name: string;
  status: string;
  tone: "ready" | "attention" | "waiting";
  owner: string;
  date: string;
  answer: string;
  evidence: { source: string; title: string; detail: string }[];
};

/** A paragraph of Session copy: plain text with emphasised names. */
export type ReviewText = (string | { strong: string })[];

/** Everything the product review exhibit renders: the Session header, the
 *  question and summary answer, and the records in the pinned View. */
export type ReviewExhibitContent = {
  title: string;
  area: string;
  question: string;
  view: string;
  recordLabel: string;
  dateLabel: string;
  summary: ReviewText[];
  nextStep: string;
  records: ReviewRecord[];
  boundary: string;
};

export type SectorContent = {
  id: "accountants" | "consultancies";
  label: string;
  buyer: string;
  title: string;
  lead: string;
  cta: string;
  sources: { label: string; icon: string }[];
  relationship: string;
  question: string;
  view: string;
  recordLabel: string;
  dateLabel: string;
  reviewTitle: string;
  reviewLead: string;
  session: Pick<ReviewExhibitContent, "title" | "area" | "summary" | "nextStep">;
  records: ReviewRecord[];
  outcomes: { title: string; body: string; from: string; to: string }[];
  boundary: string;
  questions: FaqItem[];
  closing: string;
};

export const ACCOUNTANTS: SectorContent = {
  id: "accountants", label: "Accountancy practices",
  buyer: "For managing partners and client-service teams",
  title: "Every engagement.\nA clearer next step.",
  lead: "Xero and SunSystems hold the numbers. Slack holds the questions and follow-ups. Twin connects the context, so your next client review starts with the work that needs you.",
  cta: "Discuss your practice workflow",
  sources: [
    { label: "Client invoices", icon: "xero.svg" },
    { label: "Ledger entries", icon: "sunsystems.svg" },
    { label: "Document checklist", icon: "sharepoint.svg" },
    { label: "Review dates", icon: "googlecalendar.png" },
    { label: "Team handover", icon: "slack.png" },
    { label: "Review follow-ups", icon: "slack.png" },
  ],
  relationship: "Client · Engagement · Owner",
  question: "Which engagements need attention before this week’s review?",
  view: "Practice review", recordLabel: "Engagement", dateLabel: "Review",
  reviewTitle: "See what is ready.\nSee what is missing.",
  reviewLead: "Ask across the practice. See the answer beside the full engagement view, then open a record to inspect the missing item, review date and owner.",
  session: {
    title: "Weekly engagement review", area: "Practice operations",
    summary: [
      ["Three engagements are on this week’s review list. Orbit Education’s checklist is complete. Two need attention before the partner review."],
      [{ strong: "Alder & Co" }, " is missing a supporting schedule ahead of Thursday’s review. ", { strong: "Priya Shah" }, " owns the request. ", { strong: "Meridian Foods" }, " has no recorded follow-up owner for Friday."],
    ],
    nextStep: "Start with the missing schedule and the unassigned follow-up. Keep both gaps visible in the review brief.",
  },
  records: [
    { name: "Alder & Co", status: "One item missing", tone: "attention", owner: "Priya Shah", date: "Thu",
      answer: "Alder & Co has a partner review on Thursday. The supporting schedule is not yet marked as received. Priya owns the follow-up.",
      evidence: [
        { source: "Checklist", title: "Supporting schedule outstanding", detail: "Engagement AC-026 · 11 of 12 required items recorded. The supporting schedule remains open." },
        { source: "Milestone", title: "Partner review · Thursday", detail: "Engagement tracker · Review scheduled for Thursday. Engagement manager: Priya Shah." },
        { source: "Follow-up", title: "Client request recorded", detail: "Slack review thread · Priya recorded her request for the remaining schedule. The schedule is not yet marked as received." },
      ] },
    { name: "Orbit Education", status: "Checklist complete", tone: "ready", owner: "Daniel Cole", date: "Fri",
      answer: "All 10 checklist items are marked received. Daniel can prepare Friday’s partner review. Account approval still rests with the partner.",
      evidence: [
        { source: "Checklist", title: "10 of 10 items recorded", detail: "Engagement OE-041 · All required checklist items are marked received. The accounts still need the partner’s review and approval." },
        { source: "Milestone", title: "Partner review · Friday", detail: "Engagement tracker · Friday review, owned by Daniel Cole." },
      ] },
    { name: "Meridian Foods", status: "Owner to confirm", tone: "waiting", owner: "Unassigned", date: "Fri",
      answer: "Meridian has a review on Friday, but the next follow-up has no recorded owner. Confirm responsibility before treating the engagement as ready.",
      evidence: [
        { source: "Action", title: "Follow-up owner not recorded", detail: "Engagement MF-018 · The open checklist follow-up has an empty owner field." },
        { source: "Milestone", title: "Partner review · Friday", detail: "Engagement tracker · Review due Friday. Confirm who will follow up before the review." },
      ] },
  ],
  outcomes: [
    { title: "Spot the preparation gap.", body: "See the missing evidence before the partner review.", from: "Individual preparation", to: "A consistent review" },
    { title: "Make the follow-up clear.", body: "Keep the next action and its owner beside the client record.", from: "A checklist gap", to: "A named next step" },
    { title: "Keep judgement with the partner.", body: "Check the source evidence. Your team makes the call.", from: "An answer", to: "Its source record" },
  ],
  boundary: "Example practice records. A complete checklist is not an audit opinion, tax advice or approval of accounts. We agree client access before live use.",
  questions: [
    { q: "Does this replace our practice-management system?", a: "Keep the systems you already use. We agree which records to connect and any integration work needed for your first review." },
    { q: "Can we use it across all our clients?", a: "First, agree which clients each person can access. We check those permissions before connecting live records. Reusing a review format does not give someone access to another client’s data." },
    { q: "Will it provide tax advice or sign off accounts?", a: "This workflow helps prepare the review. Your team makes the professional judgements, signs off accounts and remains responsible for advice." },
    { q: "Can we offer Twin to our clients?", a: "Discuss a partnership with Mercury Labs. We agree delivery, support and commercial terms separately from using the Twin in your own practice." },
  ],
  closing: "Start with the client question\nyour team keeps chasing.",
};

export const CONSULTANCIES: SectorContent = {
  id: "consultancies", label: "Consultancies",
  buyer: "For consulting partners, operations directors and delivery leads",
  title: "Know the delivery story.\nBefore the client call.",
  lead: "The plan says one thing. The last meeting adds another. Twin connects milestones, decisions and conversations, so your team can see what changed and what needs a decision.",
  cta: "Discuss your delivery workflow",
  sources: [
    { label: "Project milestones", icon: "linear.png" },
    { label: "Client conversations", icon: "teams.svg" },
    { label: "Decision log", icon: "notion.svg" },
    { label: "Meeting notes", icon: "granola.png" },
    { label: "Agreed scope", icon: "google-docs.png" },
    { label: "Delivery handover", icon: "slack.png" },
  ],
  relationship: "Client · Project · Decision",
  question: "What changed since our last review, and what needs a decision?",
  view: "Delivery review", recordLabel: "Workstream", dateLabel: "Milestone",
  reviewTitle: "A status is useful.\nThe reason is better.",
  reviewLead: "Ask across delivery. See the answer beside the workstream view, then open a record to inspect the decision, dependency and follow-up owner.",
  session: {
    title: "Client delivery review", area: "Delivery operations",
    summary: [
      ["Three workstreams need a look in this review. Alder’s discovery scope is agreed. Meridian’s launch and Orbit’s handover still need a decision."],
      [{ strong: "Meridian" }, " is waiting for final client sign-off. ", { strong: "Alex Morgan" }, " owns the follow-up; the launch date is unconfirmed. ", { strong: "Orbit" }, " has no receiving delivery lead recorded for Tuesday’s handover."],
    ],
    nextStep: "Confirm the client decision and the receiving lead before making the next delivery commitment.",
  },
  records: [
    { name: "Meridian · Launch", status: "Client input needed", tone: "attention", owner: "Alex Morgan", date: "Fri",
      answer: "The Sheets plan lists Friday’s launch, but the client’s Teams update says final checks are still under way. Alex’s follow-up in Docs is to confirm sign-off on Thursday. The launch date is not yet confirmed.",
      evidence: [
        { source: "Plan", title: "Launch milestone · Friday", detail: "Google Sheets · Meridian launch plan ML-028 · Target: Friday. Final client sign-off is required before launch." },
        { source: "Decision", title: "Client checks still under way", detail: "Microsoft Teams · Wednesday client update: ‘We are still completing final checks. Please wait for our sign-off before confirming Friday.’" },
        { source: "Owner", title: "Alex to confirm approval", detail: "Google Docs · Launch actions ML-028 · Alex Morgan to ask for sign-off on Thursday and update the launch plan after the client replies." },
      ] },
    { name: "Alder · Discovery", status: "Scope agreed", tone: "ready", owner: "Sam Patel", date: "Next Mon",
      answer: "Alder’s discovery scope was agreed at the last review. Sam owns the next milestone. The agreed scope is in the decision log, ready for the next review.",
      evidence: [
        { source: "Decision", title: "Discovery scope agreed", detail: "Decision log AD-011 · The discovery workstream and deliverables were agreed at the client review." },
        { source: "Plan", title: "Discovery playback · Next Monday", detail: "Milestone plan · Sam Patel owns Monday’s playback under the agreed scope." },
      ] },
    { name: "Orbit · Handover", status: "Owner to confirm", tone: "waiting", owner: "Unassigned", date: "Next Tue",
      answer: "Orbit’s handover is scheduled for Tuesday. The checklist exists, but the receiving delivery lead is not recorded. Confirm the owner before calling the handover complete.",
      evidence: [
        { source: "Handover", title: "Receiving lead not recorded", detail: "Handover checklist OH-014 · The receiving delivery-lead field is empty. Working notes and decisions are linked." },
        { source: "Plan", title: "Handover · Next Tuesday", detail: "Delivery plan · Handover is scheduled for Tuesday. Completion has not been recorded." },
      ] },
  ],
  outcomes: [
    { title: "See what changed.", body: "Read the latest client decision alongside the delivery plan.", from: "Notes and milestones", to: "One delivery position" },
    { title: "Name the next move.", body: "See which decision is pending and who is following up.", from: "An at-risk status", to: "The unresolved decision" },
    { title: "Keep the evidence close.", body: "Check the agreed scope before making the next commitment.", from: "A change of lead", to: "A shared starting point" },
  ],
  boundary: "Example project records. Your team approves client updates, scope changes and delivery commitments. We agree client and project access before live use.",
  questions: [
    { q: "Does this replace our project-management tools?", a: "Keep the plans, notes and systems your team already uses. We agree which records to connect and any integration work needed for your first delivery review." },
    { q: "Can we work across multiple clients?", a: "Agree which clients and projects each person can see. We check that access before creating a cross-client review. Reusing a review format does not give someone access to another client’s data." },
    { q: "Will it make commitments to our clients?", a: "It prepares a review for your delivery lead. Your team checks the sources and approves client messages, scope changes and delivery commitments." },
    { q: "Can we deliver Twin as part of our consulting services?", a: "Discuss a partnership with Mercury Labs. We agree delivery, support and commercial terms separately from using the Twin inside your consultancy." },
  ],
  closing: "Start with the project question\nyour team keeps chasing.",
};
